import React, { useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { HardHat, Lock, MessageSquare, Send, Camera, Calendar, Download, Paperclip, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientPortalPage() {
  const { projectId } = useParams();
  const [password, setPassword] = useState('');
  const [project, setProject] = useState(null);
  const [error, setError] = useState('');
  const [msgText, setMsgText] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [tab, setTab] = useState('geral');
  const [photoCaption, setPhotoCaption] = useState('');

  const handleAccess = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const { data } = await axios.post(`${API}/api/portal/access`, { project_id: projectId, password });
      setProject(data);
    } catch (err) { setError(err.response?.data?.detail || 'Erro ao aceder'); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    try {
      const { data } = await axios.post(`${API}/api/portal/${projectId}/message`, { password, text: msgText, author: authorName || 'Cliente' });
      setProject(p => ({ ...p, messages: [...(p.messages || []), data] }));
      setMsgText('');
    } catch {}
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const { data } = await axios.post(`${API}/api/portal/${projectId}/photo`, { password, image_base64: ev.target.result.split(',')[1], caption: photoCaption, author: authorName || 'Cliente' });
        setProject(p => ({ ...p, photos: [...(p.photos || []), data] }));
        setPhotoCaption('');
      } catch {}
    };
    reader.readAsDataURL(file);
  };

  const downloadBudget = () => {
    if (!project?.budget) return;
    const b = project.budget;
    let text = `ORCAMENTO - ${project.project_name}\n`;
    text += `Cliente: ${b.client_name}\nLocal: ${b.location}\n`;
    text += `Descricao: ${b.description}\n\n`;
    text += `ITENS:\n`;
    (b.items || []).forEach((item, i) => {
      text += `${i + 1}. ${item.descricao} - ${item.quantidade} ${item.unidade} x ${item.preco_unitario} EUR = ${item.subtotal} EUR\n`;
    });
    text += `\nTOTAL: ${b.total} EUR\n`;
    text += `Dias Estimados: ${b.estimated_days}\nFuncionarios: ${b.estimated_workers}\n`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orcamento-${project.project_name}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F7F8FA' }}>
        <div className="w-full max-w-sm pro-card-padded animate-in">
          <div className="flex items-center gap-2.5 mb-6">
            <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: '#1E3A5F' }}>
              <HardHat size={20} color="#fff" />
            </div>
            <div>
              <h1 className="text-base font-bold" style={{ color: '#1E3A5F' }}>Portal do Cliente</h1>
              <p className="text-[10px] font-medium" style={{ color: '#8C95A4' }}>PRUMO | Acompanhamento de Obra</p>
            </div>
          </div>
          {error && <div className="p-3 mb-4 rounded-md text-sm font-medium" style={{ background: '#FDECEB', color: '#C4302B' }}>{error}</div>}
          <form onSubmit={handleAccess} className="space-y-3">
            <div>
              <label className="field-label">Senha de Acesso</label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8C95A4' }} />
                <input data-testid="portal-password-input" type="password" className="input pl-9" placeholder="Introduza a senha" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
            </div>
            <div>
              <label className="field-label">Seu Nome</label>
              <input data-testid="portal-author-input" className="input" placeholder="Nome" value={authorName} onChange={e => setAuthorName(e.target.value)} />
            </div>
            <button data-testid="portal-access-btn" type="submit" className="btn btn-primary w-full">Aceder</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F7F8FA' }}>
      <div className="max-w-4xl mx-auto animate-in">
        <div className="pro-card-padded mb-4" style={{ borderLeft: '4px solid #1E3A5F' }}>
          <div className="flex items-center gap-2.5 mb-2">
            <HardHat size={24} style={{ color: '#1E3A5F' }} />
            <div>
              <h1 className="text-lg font-bold">{project.project_name}</h1>
              <p className="text-xs" style={{ color: '#5A6474' }}>{project.client_name} | {project.location}</p>
            </div>
          </div>

          {/* Project timeline and value info for client */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 mb-2">
            <div className="p-2 rounded-md" style={{ background: '#f7f9fc' }}>
              <p className="text-[10px] font-medium" style={{ color: '#8C95A4' }}>Estado</p>
              <span className={`badge ${project.status === 'concluido' ? 'badge-success' : project.status === 'em_curso' ? 'badge-warning' : 'badge-info'}`}>{project.status}</span>
            </div>
            <div className="p-2 rounded-md" style={{ background: '#f7f9fc' }}>
              <p className="text-[10px] font-medium" style={{ color: '#8C95A4' }}>Prazo Estimado</p>
              <p className="text-sm font-bold">{project.estimated_days || '?'} dias</p>
            </div>
            <div className="p-2 rounded-md" style={{ background: '#f7f9fc' }}>
              <p className="text-[10px] font-medium" style={{ color: '#8C95A4' }}>Inicio</p>
              <p className="text-sm font-semibold">{project.start_date || 'A definir'}</p>
            </div>
            <div className="p-2 rounded-md" style={{ background: '#f7f9fc' }}>
              <p className="text-[10px] font-medium" style={{ color: '#8C95A4' }}>Conclusao Est.</p>
              <p className="text-sm font-semibold">{project.end_date || 'A definir'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="p-2 rounded-md" style={{ background: '#f7f9fc' }}>
              <p className="text-[10px] font-medium" style={{ color: '#8C95A4' }}>Valor Orcamentado</p>
              <p className="text-sm font-bold" style={{ color: '#1E3A5F' }}>{(project.budget_total || 0).toLocaleString('pt-PT')} EUR</p>
            </div>
            <div className="p-2 rounded-md" style={{ background: '#f7f9fc' }}>
              <p className="text-[10px] font-medium" style={{ color: '#8C95A4' }}>Valor Atual</p>
              <p className="text-sm font-bold" style={{ color: project.total_spent > project.budget_total ? '#C4302B' : '#0D7C3E' }}>{(project.total_spent || 0).toLocaleString('pt-PT')} EUR</p>
            </div>
          </div>

          {/* Progress bar if start_date exists */}
          {project.start_date && project.estimated_days > 0 && (() => {
            const start = new Date(project.start_date);
            const now = project.end_date ? new Date(project.end_date) : new Date();
            const actual = Math.ceil((now - start) / (1000 * 60 * 60 * 24));
            const pct = Math.min(100, Math.round((actual / project.estimated_days) * 100));
            const over = actual > project.estimated_days;
            return (
              <div className="mb-2">
                <div className="flex justify-between text-[10px] font-medium mb-1">
                  <span style={{ color: '#8C95A4' }}>Progresso temporal</span>
                  <span style={{ color: over ? '#C4302B' : '#0D7C3E' }}>{pct}% ({actual}/{project.estimated_days} dias)</span>
                </div>
                <div className="w-full h-2 rounded-full" style={{ background: '#e2e8f0' }}>
                  <div className="h-2 rounded-full" style={{ width: `${Math.min(pct, 100)}%`, background: over ? '#C4302B' : '#0D7C3E' }} />
                </div>
              </div>
            );
          })()}

          {/* Tips from AI */}
          {project.ai_tips && (
            <div className="p-2 rounded-md mt-2" style={{ background: '#fffde7', border: '1px solid #fff9c4' }}>
              <p className="text-[10px] font-semibold mb-0.5" style={{ color: '#f59e0b' }}>Dica</p>
              <p className="text-xs" style={{ color: '#92400e' }}>{project.ai_tips}</p>
            </div>
          )}

          <div className="flex gap-2 mt-3">
            {project.budget && <button data-testid="download-budget-btn" onClick={downloadBudget} className="btn btn-outline btn-xs"><Download size={12} /> Descarregar Orcamento</button>}
          </div>
        </div>

        <div className="flex gap-0 mb-4 overflow-x-auto" style={{ borderBottom: '1px solid #D8DDE5' }}>
          {[
            { id: 'geral', label: 'Geral' },
            { id: 'fotos', label: 'Fotos' },
            { id: 'ficheiros', label: 'Ficheiros' },
            { id: 'mensagens', label: 'Mensagens' },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`tab-btn ${tab === t.id ? 'active' : ''}`}>{t.label}</button>
          ))}
        </div>

        {tab === 'geral' && (
          <div className="space-y-4">
            {project.description && (
              <div className="pro-card-padded">
                <h3 className="text-sm font-semibold mb-2">Descricao</h3>
                <p className="text-sm" style={{ color: '#5A6474' }}>{project.description}</p>
              </div>
            )}
            {project.timeline?.length > 0 && (
              <div className="pro-card-padded">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold flex items-center gap-1.5"><Calendar size={14} /> Cronograma da Obra</h3>
                  <button
                    onClick={() => {
                      const tasks = project.timeline || [];
                      const rows = tasks.map((t, i) => {
                        const statusLabel = t.status === 'concluido' ? 'Concluida' : t.status === 'em_curso' ? 'Em Curso' : 'Pendente';
                        const statusColor = t.status === 'concluido' ? '#0B7A3E' : t.status === 'em_curso' ? '#C67A09' : '#94A3B8';
                        const prog = t.status === 'concluido' ? 100 : (t.progress || 0);
                        return `<tr style="background:${i%2===0?'#f7f9fc':'#fff'}">
                          <td>${i+1}</td><td>${t.task}</td><td>${t.start_date||'—'}</td><td>${t.end_date||'—'}</td>
                          <td style="color:${statusColor};font-weight:bold">${statusLabel}</td>
                          <td>
                            <div style="background:#e2e8f0;height:8px;border-radius:4px;width:100px">
                              <div style="background:${statusColor};height:8px;border-radius:4px;width:${prog}%"></div>
                            </div>
                            <span style="font-size:10px;color:${statusColor}">${prog}%</span>
                          </td>
                        </tr>`;
                      }).join('');
                      const win = window.open('', '_blank');
                      win.document.write(`<!DOCTYPE html><html><head><title>Cronograma</title><meta charset="utf-8">
                        <style>body{font-family:Arial,sans-serif;padding:20px;color:#1a202c}h1{color:#0F2B4E}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#0F2B4E;color:#fff;padding:6px 8px}td{padding:5px 8px;border-bottom:1px solid #e2e8f0}.footer{margin-top:20px;font-size:10px;color:#94a3b8;text-align:center}@media print{button{display:none}}</style></head>
                        <body><h1>Cronograma de Obra</h1><p style="color:#5A6474;font-size:12px">${project.project_name} | ${project.client_name}</p>
                        <button onclick="window.print()" style="margin:12px 0;padding:8px 16px;background:#1A5FAB;color:white;border:none;border-radius:4px;cursor:pointer">Imprimir</button>
                        <table><thead><tr><th>#</th><th>Tarefa</th><th>Inicio</th><th>Conclusao</th><th>Estado</th><th>Progresso</th></tr></thead><tbody>${rows}</tbody></table>
                        <div class="footer">PRUMO software | Criado por: Rui Guimaraes | ${new Date().toLocaleDateString('pt-PT')}</div>
                        </body></html>`);
                      win.document.close();
                    }}
                    className="btn btn-outline btn-xs"
                  >
                    Imprimir Cronograma
                  </button>
                </div>
                <div className="space-y-2">
                  {project.timeline.map((t, i) => {
                    const prog = t.status === 'concluido' ? 100 : (t.progress || 0);
                    return (
                      <div key={i} className="p-3 rounded-md" style={{ background: '#F0F2F5' }}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${t.status === 'concluido' ? 'bg-green-500' : t.status === 'em_curso' ? 'bg-yellow-500' : 'bg-gray-300'}`} />
                          <div className="flex-1">
                            <p className="text-sm font-semibold">{t.task}</p>
                            <p className="text-[10px]" style={{ color: '#8C95A4' }}>
                              {t.start_date && t.end_date ? `${t.start_date} → ${t.end_date}` : t.estimated_days ? `${t.estimated_days} dias estimados` : ''}
                            </p>
                          </div>
                          <span className={`badge ${t.status === 'concluido' ? 'badge-success' : t.status === 'em_curso' ? 'badge-warning' : 'badge-neutral'}`}>
                            {t.status === 'concluido' ? 'Concluida' : t.status === 'em_curso' ? 'Em Curso' : 'Pendente'}
                          </span>
                        </div>
                        <div className="w-full h-1.5 rounded-full" style={{ background: '#e2e8f0' }}>
                          <div className="h-1.5 rounded-full" style={{ width: `${prog}%`, background: t.status === 'concluido' ? '#0B7A3E' : t.status === 'em_curso' ? '#C67A09' : '#94A3B8' }} />
                        </div>
                        <p className="text-[10px] text-right mt-0.5" style={{ color: '#8C95A4' }}>{prog}%</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {project.budget && (
              <div className="pro-card-padded">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1.5"><FileText size={14} /> Orcamento</h3>
                {project.budget.items?.length > 0 && (
                  <table className="pro-table">
                    <thead><tr><th>Descricao</th><th>Qtd</th><th>Preco</th><th>Subtotal</th></tr></thead>
                    <tbody>
                      {project.budget.items.map((item, i) => (
                        <tr key={i}><td>{item.descricao}</td><td>{item.quantidade} {item.unidade}</td><td>{item.preco_unitario} EUR</td><td className="font-semibold">{item.subtotal} EUR</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <div className="text-right mt-2"><span className="text-base font-bold" style={{ color: '#1E3A5F' }}>Total: {project.budget.total} EUR</span></div>
              </div>
            )}
          </div>
        )}

        {tab === 'fotos' && (
          <div className="pro-card-padded">
            <div className="flex gap-2 mb-4 items-end flex-wrap">
              <input className="input flex-1 min-w-[150px]" placeholder="Legenda" value={photoCaption} onChange={e => setPhotoCaption(e.target.value)} />
              <label className="btn btn-primary btn-sm cursor-pointer"><Camera size={14} /> Enviar Foto<input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} /></label>
            </div>
            {project.photos?.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {project.photos.map((p, i) => (
                  <div key={p.id || i} className="pro-card overflow-hidden">
                    {p.image_base64 && <img src={`data:image/jpeg;base64,${p.image_base64}`} alt={p.caption} className="w-full h-36 object-cover" />}
                    <div className="p-2"><p className="text-xs font-medium">{p.caption || 'Sem legenda'}</p><p className="text-[10px]" style={{ color: '#8C95A4' }}>{p.uploaded_by} - {p.uploaded_at}</p></div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-center py-6" style={{ color: '#8C95A4' }}>Sem fotos</p>}
          </div>
        )}

        {tab === 'ficheiros' && (
          <div className="pro-card-padded">
            {project.files?.length > 0 ? (
              <div className="space-y-2">
                {project.files.map(f => (
                  <div key={f.id} className="flex items-center justify-between p-3 rounded-md" style={{ background: '#F0F2F5' }}>
                    <div className="flex items-center gap-2"><Paperclip size={14} style={{ color: '#8C95A4' }} /><span className="text-sm font-medium">{f.name}</span></div>
                    <span className="text-[10px]" style={{ color: '#8C95A4' }}>{f.uploaded_at}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-center py-6" style={{ color: '#8C95A4' }}>Sem ficheiros</p>}
          </div>
        )}

        {tab === 'mensagens' && (
          <div className="pro-card-padded">
            <div className="max-h-80 overflow-y-auto scrollbar-thin mb-4 space-y-2">
              {project.messages?.length > 0 ? project.messages.map(m => (
                <div key={m.id} className={`flex ${m.author_type === 'client' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`msg-bubble ${m.author_type === 'client' ? 'msg-bubble-self' : 'msg-bubble-other'}`}>
                    <p className="text-[10px] font-semibold mb-0.5 opacity-70">{m.author}</p>
                    <p className="text-sm">{m.text}</p>
                    <p className="text-[9px] mt-1 opacity-50">{m.created_at}</p>
                  </div>
                </div>
              )) : <p className="text-sm text-center py-6" style={{ color: '#8C95A4' }}>Sem mensagens</p>}
            </div>
            <div className="flex gap-2">
              <input data-testid="portal-comment-input" className="input flex-1" placeholder="Escrever mensagem..." value={msgText} onChange={e => setMsgText(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} />
              <button data-testid="portal-send-comment-btn" onClick={sendMessage} className="btn btn-primary"><Send size={14} /></button>
            </div>
          </div>
        )}
      </div>
      <p className="text-center text-[9px] mt-4 pb-4" style={{ color: '#C8D6E8' }}>PRUMO software | Criado por: Rui Guimaraes</p>
    </div>
  );
}
